import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  getStoredLocale,
  localeDocumentDir,
  localeDocumentLang,
  normalizeLocale,
  setStoredLocale,
  translate,
} from '@/i18n'

/** @typedef {import('@/i18n').UiLocale} UiLocale */

/** @typedef {(key: string, params?: Record<string, string | number>) => string} TranslateFn */

/**
 * @typedef {{
 *   locale: UiLocale,
 *   setLocale: (locale: UiLocale) => void,
 *   t: TranslateFn,
 * }} UiLocaleContextValue
 */

const UiLocaleContext = createContext(/** @type {UiLocaleContextValue | null} */ (null))

export function UiLocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(() => getStoredLocale())

  const setLocale = useCallback((nextLocale) => {
    const normalized = normalizeLocale(nextLocale)
    setStoredLocale(normalized)
    setLocaleState(normalized)
  }, [])

  useEffect(() => {
    document.documentElement.lang = localeDocumentLang(locale)
    document.documentElement.dir = localeDocumentDir(locale)
  }, [locale])

  const t = useCallback(
    (key, params) => translate(key, locale, params),
    [locale],
  )

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  )

  return (
    <UiLocaleContext.Provider value={value}>
      {children}
    </UiLocaleContext.Provider>
  )
}

export function useUiLocale() {
  const context = useContext(UiLocaleContext)

  if (!context) {
    throw new Error('useUiLocale must be used within UiLocaleProvider')
  }

  return context
}

/**
 * Safe hook for components that may render outside the provider (e.g. tests).
 * @returns {UiLocaleContextValue}
 */
export function useUiLocaleOptional() {
  const context = useContext(UiLocaleContext)

  return context ?? {
    locale: 'en',
    setLocale: () => {},
    t: (key, params) => translate(key, 'en', params),
  }
}
